// src/components/forms/AboutYou/InclusionInfoControl.tsx
/* eslint-disable */
import { useEffect, useState } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ContainerGroupField } from '@/components/common/ContainerGroupField'
import {
    CheckboxControl,
    SelectControl,
    RadioGroupControl,
    AutocompleteControl
} from '@/components/form-controls'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'

interface InclusionInfoControlProps {
    control: Control<any>
    setValue?: (name: string, value: any) => void
    clearErrors?: (name?: any) => void
    idAspirant?: number
}

type Option = { value: string; label: string }

export const InclusionInfoControl = ({
    control,
    setValue,
    clearErrors,
    idAspirant
}: InclusionInfoControlProps): JSX.Element => {
    console.log('🔍 InclusionInfoControl component mounted with:', {
        idAspirant,
        setValue: !!setValue,
        clearErrors: !!clearErrors,
        timestamp: new Date().toISOString()
    })

    const { t } = useTranslation()

    // Datos (en memoria; si luego hay API se replica el patrón de fetch de MedicalControl)
    const [ethnicityOptions, setEthnicityOptions] = useState<Option[]>([])
    const [disabilityOptions, setDisabilityOptions] = useState<Option[]>([])
    const [originalOptions, setOriginalOptions] = useState<{
        ethnicity: any[]
        disability: any[]
    }>({ ethnicity: [], disability: [] })

    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    useEffect(() => {
        console.log('🔍 Loading inclusion options...')
        let mounted = true

        console.log('🔍 About to call loadOptions function')

        const loadOptions = async (): Promise<void> => {
            try {
                console.log('🔍 Inside loadOptions function - START')
                console.log('🔍 Fetching ethnicity and disability options...')
                console.log('🔍 About to call APIs:', {
                    resourcesApi: !!resourcesApi,
                    getEthnicityList: !!resourcesApi?.getEthnicityList,
                    getDisabilityList: !!resourcesApi?.getDisabilityList
                })

                console.log('🔍 About to call Promise.all...')
                const [eth, dis] = await Promise.all([
                    resourcesApi.getEthnicityList(),
                    resourcesApi.getDisabilityList()
                ])

                console.log(
                    '🔍 APIs called successfully, received responses:',
                    {
                        eth: eth ? 'received' : 'null',
                        dis: dis ? 'received' : 'null'
                    }
                )

                if (!mounted) {
                    console.log('🔍 Component unmounted, returning early')
                    return
                }

                console.log('🔍 Raw options received:', { eth, dis })

                const mapOpts = (arr: any[] = []) =>
                    (Array.isArray(arr) ? arr : [])
                        .filter((o) => o?.value && o?.label)
                        .map((o, i) => ({
                            value: String(o.value),
                            label: String(o.label)
                        }))

                const mappedEth = mapOpts(eth)
                const mappedDis = mapOpts(dis)

                console.log('🔍 Mapped options:', {
                    mappedEth,
                    mappedDis
                })

                // Guardar opciones originales para mapeo
                setOriginalOptions({
                    ethnicity: eth,
                    disability: dis
                })

                setEthnicityOptions(mappedEth)
                setDisabilityOptions(mappedDis)

                console.log('🔍 Options set successfully')
                console.log('🔍 About to trigger re-render with new options')
                console.log('🔍 Inside loadOptions function - END')
            } catch (e) {
                console.error('🔍 ERROR in loadOptions:', e)
                console.error('🔍 Error details:', {
                    message: (e as Error)?.message,
                    stack: (e as Error)?.stack,
                    name: (e as Error)?.name
                })
                setEthnicityOptions([])
                setDisabilityOptions([])
            }
        }

        console.log('🔍 About to call loadOptions()')
        loadOptions().catch((error) => {
            console.error('🔍 ERROR in loadOptions catch:', error)
        })

        return () => {
            console.log('🔍 useEffect cleanup - setting mounted to false')
            mounted = false
        }
    }, [])

    // Cargar datos existentes de inclusión (después de que las opciones estén listas)
    useEffect(() => {
        console.log(
            '🔍 SECOND useEffect - Inclusion useEffect triggered with:',
            {
                idAspirant,
                setValue: !!setValue,
                ethnicityOptionsLength: ethnicityOptions.length,
                disabilityOptionsLength: disabilityOptions.length
            }
        )

        if (
            !idAspirant ||
            !setValue ||
            ethnicityOptions.length === 0 ||
            disabilityOptions.length === 0
        ) {
            console.log(
                '🔍 Inclusion useEffect: Conditions not met, returning early'
            )
            return
        }

        console.log(
            '🔍 Inclusion useEffect: All conditions met, proceeding to load data'
        )

        const loadInclusionData = async () => {
            try {
                console.log(
                    '🔍 Making API call to getInclusionData for idAspirant:',
                    idAspirant
                )
                const response = await aspirantsApi.getInclusionData(idAspirant)
                console.log('🔍 Inclusion API response received:', response)
                console.log('🔍 Response status:', response?.status)
                console.log('🔍 Response data:', response?.data)
                if (response.status === 200 && response.data) {
                    const data = response.data
                    console.log('Inclusion data received:', data)
                    console.log(
                        'Ethnicity options available:',
                        ethnicityOptions
                    )
                    console.log(
                        'Disability options available:',
                        disabilityOptions
                    )

                    // Establecer dropdowns Sí/No (convertir boolean a string)
                    const ethnicValue = data.ethnic_group ? 'true' : 'false'
                    const disabilityValue = data.disability ? 'true' : 'false'

                    console.log('🔍 Converting boolean to string values:', {
                        original: {
                            ethnic_group: data.ethnic_group,
                            disability: data.disability
                        },
                        converted: { ethnicValue, disabilityValue }
                    })

                    console.log('🔍 Setting main radio button values...')
                    setValue('recognizesEthnicGroup', ethnicValue)
                    setValue('hasDisability', disabilityValue)
                    console.log('🔍 Main radio button values set')

                    console.log('Values set successfully')

                    // Forzar actualización del formulario
                    setTimeout(() => {
                        console.log('Form values after timeout:', {
                            recognizesEthnicGroup:
                                control._formValues?.recognizesEthnicGroup,
                            hasDisability: control._formValues?.hasDisability
                        })
                    }, 100)

                    // Para los dropdowns dependientes, establecer el código que vendrá del backend
                    console.log('🔍 Processing dependent fields...')

                    // Solo establecer si el valor principal es true Y hay descripción
                    if (
                        data.ethnic_group === true &&
                        data.ethnic_group_description
                    ) {
                        console.log(
                            '🔍 Ethnic group is true, looking for value:',
                            data.ethnic_group_description
                        )
                        console.log(
                            '🔍 Available ethnicity options:',
                            ethnicityOptions
                        )
                        const foundOption = ethnicityOptions.find(
                            (opt) => opt.value === data.ethnic_group_description
                        )
                        console.log(
                            '🔍 Found matching ethnicity option:',
                            foundOption
                        )
                        if (foundOption) {
                            setValue(
                                'ethnicGroup',
                                data.ethnic_group_description
                            )
                            console.log(
                                '🔍 ✅ Set ethnicGroup to:',
                                data.ethnic_group_description
                            )
                        } else {
                            console.log(
                                '🔍 ❌ No matching option found for ethnic group value:',
                                data.ethnic_group_description
                            )
                        }
                    } else {
                        console.log(
                            '🔍 Ethnic group is false or no description, clearing field'
                        )
                        setValue('ethnicGroup', '')
                    }

                    if (
                        data.disability === true &&
                        data.disability_description
                    ) {
                        console.log(
                            '🔍 Disability is true, looking for value:',
                            data.disability_description
                        )
                        console.log(
                            '🔍 Available disability options:',
                            disabilityOptions
                        )
                        const foundOption = disabilityOptions.find(
                            (opt) => opt.value === data.disability_description
                        )
                        console.log(
                            '🔍 Found matching disability option:',
                            foundOption
                        )
                        if (foundOption) {
                            setValue(
                                'disabilityType',
                                data.disability_description
                            )
                            console.log(
                                '🔍 ✅ Set disabilityType to:',
                                data.disability_description
                            )
                        } else {
                            console.log(
                                '🔍 ❌ No matching option found for disability value:',
                                data.disability_description
                            )
                        }
                    } else {
                        console.log(
                            '🔍 Disability is false or no description, clearing field'
                        )
                        setValue('disabilityType', '')
                    }
                }
            } catch (error) {
                console.error('Error loading inclusion data:', error)
            }
        }

        loadInclusionData()
    }, [idAspirant, setValue, ethnicityOptions, disabilityOptions])

    // Watches
    const recognizesEthnicGroup = useWatch({
        control,
        name: 'recognizesEthnicGroup'
    })
    const hasDisability = useWatch({ control, name: 'hasDisability' })

    // Debug: Log current values
    console.log('🔍 InclusionInfoControl current values:', {
        recognizesEthnicGroup,
        hasDisability,
        ethnicityOptionsLength: ethnicityOptions.length,
        disabilityOptionsLength: disabilityOptions.length
    })

    // Debug: Verificar que los valores se están estableciendo
    useEffect(() => {
        console.log('🔍 Form values changed:', {
            recognizesEthnicGroup,
            hasDisability,
            timestamp: new Date().toISOString()
        })
    }, [recognizesEthnicGroup, hasDisability])

    // Debug: Verificar si el componente se está renderizando con los valores correctos
    useEffect(() => {
        console.log('🔍 Component render with current values:', {
            recognizesEthnicGroup,
            hasDisability,
            ethnicityOptionsLength: ethnicityOptions.length,
            disabilityOptionsLength: disabilityOptions.length
        })
    }, [
        recognizesEthnicGroup,
        hasDisability,
        ethnicityOptions,
        disabilityOptions
    ])

    useEffect(() => {
        if (recognizesEthnicGroup !== 'true' && setValue && clearErrors) {
            setValue('ethnicGroup', '')
            clearErrors('ethnicGroup')
        }
    }, [recognizesEthnicGroup, setValue, clearErrors])

    useEffect(() => {
        if (hasDisability !== 'true' && setValue && clearErrors) {
            setValue('disabilityType', '')
            clearErrors('disabilityType')
        }
    }, [hasDisability, setValue, clearErrors])

    return (
        <ContainerGroupField
            tittleGroup={t('forms.groups.inclusionInformation')}
        >
            {/* Grupo Étnico */}
            <div className="flex flex-col md:flex-row md:gap-4 w-full">
                <div className="md:w-1/2">
                    <RadioGroupControl
                        name="recognizesEthnicGroup"
                        control={control}
                        label={t('inclusion.ethnic.question')}
                        options={[
                            { value: 'true', label: 'Sí' },
                            { value: 'false', label: 'No' }
                        ]}
                        rules={{
                            required: t(
                                'inclusion.required.recognizesEthnicGroup'
                            )
                        }}
                        onChangeCustom={(value) => {
                            if (clearErrors) {
                                clearErrors('recognizesEthnicGroup')
                            }
                        }}
                    />
                </div>
                {recognizesEthnicGroup === 'true' && (
                    <div className="md:w-1/2 mt-4 md:mt-0">
                        <SelectControl
                            name="ethnicGroup"
                            control={control}
                            label={t('inclusion.ethnic.select')}
                            options={ethnicityOptions}
                            rules={{
                                validate: (v: string) =>
                                    recognizesEthnicGroup === 'true'
                                        ? !!v ||
                                          t('inclusion.required.ethnicGroup')
                                        : true
                            }}
                            classNames={{
                                listbox:
                                    'max-h-96 overflow-y-auto scrollbar-custom'
                            }}
                            onChangeCustom={(value) => {
                                if (clearErrors) {
                                    clearErrors('ethnicGroup')
                                }
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Discapacidad */}
            <div className="flex flex-col md:flex-row md:gap-4 w-full">
                <div className="md:w-1/2">
                    <RadioGroupControl
                        name="hasDisability"
                        control={control}
                        label={t('inclusion.disability.question')}
                        options={[
                            { value: 'true', label: 'Sí' },
                            { value: 'false', label: 'No' }
                        ]}
                        rules={{
                            required: t('inclusion.required.hasDisability')
                        }}
                        onChangeCustom={(value) => {
                            if (clearErrors) {
                                clearErrors('hasDisability')
                            }
                        }}
                    />
                </div>
                {hasDisability === 'true' && (
                    <div className="md:w-1/2 mt-4 md:mt-0">
                        <AutocompleteControl
                            name="disabilityType"
                            control={control}
                            label={t('inclusion.disability.select')}
                            options={disabilityOptions}
                            rules={{
                                validate: (v: string) =>
                                    hasDisability === 'true'
                                        ? !!v ||
                                          t('inclusion.required.disabilityType')
                                        : true
                            }}
                            onChangeCustom={(value) => {
                                if (clearErrors) {
                                    clearErrors('disabilityType')
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </ContainerGroupField>
    )
}
